import * as yup from "yup";
import {capitalize} from "@mui/material";
import {FormikConfig, FormikHelpers} from "formik";
import {useCreateMandant} from "../../client/hooks";
import {useCallback, useMemo} from "react";
import {Mandant, MandantDaten} from "../../api";
import {useMandantenHandling} from "./useMandantenHandling";
import {useSnackbar} from "notistack";
import {useNavigate} from "react-router-dom";
import {useMutation, useQueryClient} from "react-query";
import type { MandantFormValues } from "../../types/mandantenTypes";
import {useOnboardingHandling} from "../onboarding/useOnboardingHandling";

export function useMandantCreateForm(): FormikConfig<MandantFormValues> {
    const {translation} = useMandantenHandling();
    const {getCountryCode} = useOnboardingHandling();
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const {mutateAsync} = useMandantCreate();

    const schema = yup.object().shape({
        id: yup.string().required(capitalize(translation('client-attributes-id-required-message'))),
        name: yup.string().required(capitalize(translation('client-attributes-name-required-message'))),
        beschreibung: yup.string().nullable(),
        prompt: yup.string().nullable(),
        externalApiUrl: yup.string().nullable().notRequired(),
        telefonnummer: yup.string()
            .transform((v) => (v === '' ? null : v))
            .nullable()
            .matches(/^[1-9][0-9]*$/, capitalize(translation('client-test-attributes-phone-validation-failed-message'))),
        speechModel: yup.string().transform((v) => (v === '' ? null : v)).nullable(),
    });

    const initialTouched = useMemo(() => ({
        id: false,
        name: false,
        beschreibung: false,
        telefonnummer: false,
        prompt: false,
        externalApiUrl: false,
        speechModel: false,
    }), []);

    const initialValues = useMemo(() => ({
        id: '',
        name: '',
        beschreibung: '',
        telefonnummer: '',
        prompt: '',
        externalApiUrl: '',
        speechModel: '',
    }), []);

    const onSubmitCreateForm = useCallback(async (
        daten: MandantFormValues,
        {setSubmitting, setStatus}: FormikHelpers<MandantFormValues>
    ): Promise<void> => {
        try {
            const mandant: Mandant = await mutateAsync({
                id: daten.id,
                name: daten.name,
                beschreibung: daten.beschreibung,
                prompt: daten.prompt,
                telefonnummer: daten.telefonnummer?.trim() ? `${getCountryCode()}${daten.telefonnummer.trim()}` : null,
                external_api_url: daten.externalApiUrl && daten.externalApiUrl.length > 0 ? daten.externalApiUrl : undefined,
                speechModel: daten.speechModel || undefined,
            });
            setStatus(undefined);
            enqueueSnackbar(capitalize(translation('client-creation-successful')), {
                variant: "success",
            });
            navigate(`/mandanten/${mandant.id}`);
        } catch (error: unknown) {
            enqueueSnackbar(capitalize(translation('client-creation-failed')), {
                variant: "error",
            });
            if (error instanceof Error) {
                setStatus(error.message);
            } else {
                setStatus("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [mutateAsync, enqueueSnackbar, translation, navigate]);

    return useMemo(
        () => ({
            validationSchema: schema,
            validateOnMount: true,
            enableReinitialize: true,
            initialTouched: initialTouched,
            initialValues: initialValues,
            onSubmit: onSubmitCreateForm}),
        [schema, initialValues, initialTouched, onSubmitCreateForm]
    );
}

function useMandantCreate() {
    const createMandant = useCreateMandant();
    const queryClient = useQueryClient();

    return useMutation(
        (input: MandantDaten): Promise<Mandant> => {
            return createMandant(input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["mandanten"]);
            },
        }
    );
}